// Minimal direct read of storage-hub file metadata by id, used only to
// repopulate column dropdowns when reopening a saved reconciliation (the normal
// pick flow already holds the metadata in memory). The file resource is
// workspace-nested (`/workspaces/{wsid}/files/{id}`). Behind the real API Gateway the
// authorizer requires an Authorization: Bearer token (the X-Authz-* claims are
// derived downstream, not the credential) — so we attach the bearer when a
// token getter is registered (by AuthBridge). Best-effort: any failure returns
// undefined so the editor falls back to manual column entry.

import { isAuthRecoverable, DEV_TOKEN } from '@micronet/auth';
import { ENV } from '../config/env';
import { useStore } from '../store/useStore';

export interface FetchedFile {
  filename?: string;
  /** the storage-hub file_metadata blob (columns + grid config). */
  metadata?: Record<string, unknown>;
}

let getAccessToken: ((forceRefresh?: boolean) => Promise<string>) | undefined;

// configureStorageHub registers the access-token getter (mirrors api/client.ts's
// configure). AuthBridge calls it so fetchFileMetadata can attach the bearer.
export function configureStorageHub(opts: {
  getAccessToken?: (forceRefresh?: boolean) => Promise<string>;
}): void {
  if ('getAccessToken' in opts) getAccessToken = opts.getAccessToken;
}

// Performs the GET once. `sentBearer` reports whether a real (non-dev) token was
// attached, so the caller only bothers force-refreshing when there was actually
// a token the backend could have rejected.
async function fetchOnce(
  fileId: string,
  forceRefresh: boolean
): Promise<{ res: Response; sentBearer: boolean }> {
  // Not merely narrowed for the type: String(null) and the path interpolation below
  // both accept null happily, so an unresolved workspace would GET
  // /workspaces/null/files/<id> and read storage-hub's 404 as a missing file.
  const workspaceId = useStore.getState().workspaceId;
  if (workspaceId === null) throw new Error('No hay un workspace activo.');
  const headers: Record<string, string> = {
    'X-Authz-User-Id': String(ENV.authzUserId),
    'X-Authz-Workspace-Ids': String(workspaceId),
  };
  let sentBearer = false;
  if (getAccessToken) {
    const token = await getAccessToken(forceRefresh);
    if (token && token !== DEV_TOKEN) {
      headers['Authorization'] = `Bearer ${token}`;
      sentBearer = true;
    }
  }
  const res = await fetch(`${ENV.storagehubApi}/workspaces/${workspaceId}/files/${fileId}`, {
    headers,
  });
  return { res, sentBearer };
}

// Best-effort read of the {code} field from a JSON error body. Consuming the
// failed response's body is fine — it is discarded either way.
async function errorCode(res: Response): Promise<string | undefined> {
  try {
    return ((await res.json()) as { code?: string }).code;
  } catch {
    return undefined;
  }
}

export async function fetchFileMetadata(fileId: string): Promise<FetchedFile | undefined> {
  try {
    const { res: firstRes, sentBearer } = await fetchOnce(fileId, false);
    let res = firstRes;
    // One forced-refresh retry when the backend rejected our token mid-session
    // (401, or a 403 that isn't an RBAC "forbidden" denial). Best-effort: any
    // remaining failure falls through to the warn + undefined below.
    if (!res.ok && sentBearer && isAuthRecoverable(res.status, await errorCode(res))) {
      res = (await fetchOnce(fileId, true)).res;
    }
    if (!res.ok) {
      // Warn (not swallow) so an auth/gateway misconfig (401/403) is not mistaken
      // for "file has no metadata" when debugging empty column dropdowns.
      console.warn(`fetchFileMetadata: ${res.status} for file ${fileId}`);
      return undefined;
    }
    const body = (await res.json()) as {
      filename?: string;
      file_metadata?: Record<string, unknown> | null;
    };
    return { filename: body.filename, metadata: body.file_metadata ?? undefined };
  } catch (e) {
    console.warn(`fetchFileMetadata: request failed for file ${fileId}`, e);
    return undefined;
  }
}
